/**
 * Collection Dynamic Discount Badge
 * Updates discount badge based on hovered/selected product in collections
 * Specifically designed for product collection grids
 */

class CollectionDynamicBadge {
  constructor() {
    this.badgeSelector = '.discount-badge-dynm__percent-value';
    this.productGridSelector = '.collection-products, [data-collection-products], .product-grid';
    this.productItemSelector = '.productitem, [data-product-item], .product-item';
    this.currentBadgeData = null;
    
    console.log('🎯 CollectionDynamicBadge: Initializing for product collection...');
    this.init();
  }

  init() {
    this.bindCollectionEvents();
    this.extractInitialBadgeData();
    this.setupBadgePositioning();
  }

  /**
   * Extract initial badge data from all products
   */
  extractInitialBadgeData() {
    const products = document.querySelectorAll(this.productItemSelector);
    console.log(`📦 Found ${products.length} products in collection`);
    
    for (const product of products) {
      const discountData = this.extractDiscountData(product);
      if (discountData) {
        // Update each product's badge individually
        this.updateBadge(discountData, product);
      }
    }
  }

  /**
   * Setup badge positioning and visibility
   */
  setupBadgePositioning() {
    const badges = document.querySelectorAll('.discount-badge-dynm');
    badges.forEach((badge, index) => {
      console.log(`Badge ${index + 1} positioning:`, {
        display: badge.style.display,
        position: window.getComputedStyle(badge).position,
        parent: badge.parentElement?.className
      });
    });
  }

  /**
   * Bind events specifically for collection behavior
   */
  bindCollectionEvents() {
    console.log('🔗 Binding collection-specific events...');
    
    // Handle mouse enter/leave for desktop
    document.addEventListener('mouseenter', (e) => {
      const productElement = e.target.closest(this.productItemSelector);
      if (productElement && this.isInCollection(productElement)) {
        this.handleProductHover(productElement);
      }
    }, true);

    document.addEventListener('pointerdown', (e) => {
      const productElement = e.target.closest(this.productItemSelector);
      if (productElement && this.isInCollection(productElement)) {
        this.handleProductClick(productElement);
      }
    });

    document.addEventListener('focusin', (e) => {
      const productElement = e.target.closest(this.productItemSelector);
      if (productElement && this.isInCollection(productElement)) {
        this.handleProductClick(productElement);
      }
    });

    // Handle click for mobile/touch devices
    document.addEventListener('click', (e) => {
      const productElement = e.target.closest(this.productItemSelector);
      if (productElement && this.isInCollection(productElement)) {
        this.handleProductClick(productElement);
      }
    });

    // Handle variant changes within collection products
    document.addEventListener('change', (e) => {
      if (e.target.matches('[name="id"], .variant-selector, [data-variant-selector]')) {
        const productElement = e.target.closest(this.productItemSelector);
        if (productElement && this.isInCollection(productElement)) {
          this.handleVariantChange(productElement);
        }
      }
    });
  }

  /**
   * Check if element is within a collection
   */
  isInCollection(element) {
    return element.closest(this.productGridSelector) !== null;
  }

  /**
   * Handle product hover (desktop)
   */
  handleProductHover(productElement) {
    console.log('🖱️ Product hovered:', productElement);
    this.scheduleUpdate(productElement);
  }

  /**
   * Handle product leave (no reset to avoid one-click lag)
   */
  handleProductLeave() {
    console.log('🚪 Mouse left product area');
  }

  /**
   * Handle product click (mobile/touch)
   */
  handleProductClick(productElement) {
    console.log('📱 Product clicked:', productElement);
    this.scheduleUpdate(productElement);
  }

  /**
   * Handle variant changes within a product
   */
  handleVariantChange(productElement) {
    console.log('🔄 Variant changed for product:', productElement);
    this.scheduleUpdate(productElement);
  }

  scheduleUpdate(productElement) {
    if (this.pendingUpdate) {
      cancelAnimationFrame(this.pendingUpdate);
    }
    this.pendingElement = productElement;
    this.pendingUpdate = requestAnimationFrame(() => {
      const discountData = this.extractDiscountData(this.pendingElement);
      if (discountData) {
        this.updateBadge(discountData, this.pendingElement);
        this.highlightProduct(this.pendingElement);
        this.currentBadgeData = discountData;
      }
    });
  }

  /**
   * Extract discount data from product element
   */
  extractDiscountData(productElement) {
    console.log('🔍 Extracting discount data from product:', productElement);
    
    // Get product info from data attributes
    const productId = productElement.dataset.productId;
    
    // First try to get variant discounts data from badge
    const badgeElement = productElement.querySelector('.discount-badge-dynm');
    const variantDiscounts = badgeElement?.dataset.variantDiscounts;
    
    if (variantDiscounts) {
      console.log('Found variant discounts data:', variantDiscounts);
      return this.getDiscountFromVariantData(productElement, variantDiscounts);
    }
    
    // Fallback to current variant selection
    const selectedVariant = this.getSelectedVariant(productElement);
    
    let price, compareAtPrice;
    
    if (selectedVariant) {
      // Use selected variant data
      price = selectedVariant.price;
      compareAtPrice = selectedVariant.compareAtPrice;
    } else {
      // Fallback to product-level data
      price = this.extractPrice(productElement);
      compareAtPrice = this.extractCompareAtPrice(productElement);
    }
    
    console.log('Extracted prices:', { price, compareAtPrice, selectedVariant });
    
    if (price && compareAtPrice && compareAtPrice > price) {
      const discount = compareAtPrice - price;
      const percentage = (discount / compareAtPrice) * 100;
      const roundedPercentage = Math.max(0, Math.min(100, Math.round(percentage)));
      
      return {
        productId,
        price,
        compareAtPrice,
        discountPercentage: roundedPercentage,
        hasDiscount: true
      };
    }
    
    return {
      productId,
      price: price || 0,
      compareAtPrice: compareAtPrice || 0,
      discountPercentage: 0,
      hasDiscount: false
    };
  }

  /**
   * Get discount from variant data stored in badge
   */
  getDiscountFromVariantData(productElement, variantDiscounts) {
    const variants = variantDiscounts.split('|');
    const selectedVariant = this.getSelectedVariant(productElement);
    
    if (selectedVariant) {
      // Find matching variant
      for (const variantData of variants) {
        const [variantId, discount, price, comparePrice] = variantData.split(':');
        // For now, use the first variant's discount (can be enhanced to match by ID)
        if (discount && parseInt(discount) > 0) {
          return {
            productId: productElement.dataset.productId,
            price: parseFloat(price),
            compareAtPrice: parseFloat(comparePrice),
            discountPercentage: parseInt(discount),
            hasDiscount: true
          };
        }
      }
    }
    
    // Fallback to first variant with discount
    for (const variantData of variants) {
      const [variantId, discount, price, comparePrice] = variantData.split(':');
      if (discount && parseInt(discount) > 0) {
        return {
          productId: productElement.dataset.productId,
          price: parseFloat(price),
          compareAtPrice: parseFloat(comparePrice),
          discountPercentage: parseInt(discount),
          hasDiscount: true
        };
      }
    }
    
    return null;
  }

  /**
   * Get currently selected variant from dropdowns
   */
  getSelectedVariant(productElement) {
    const variantSelect = productElement.querySelector('select[name="id"], .variant-selector, [data-variant-selector]');
    if (!variantSelect) return null;
    
    const selectedOption = variantSelect.options[variantSelect.selectedIndex];
    if (!selectedOption) return null;
    
    const price = this.parsePrice(selectedOption.dataset.price || selectedOption.textContent);
    const compareAtPrice = this.parsePrice(selectedOption.dataset.comparePrice || selectedOption.textContent);
    
    if (price && compareAtPrice) {
      return { price, compareAtPrice };
    }
    
    return null;
  }

  /**
   * Extract current price from product element
   */
  extractPrice(productElement) {
    const priceSelectors = [
      '.price--current .price-item, .price-item--regular, [data-price]',
      '.product-price--current',
      '.price .money',
      '[data-product-price]',
      '.price-current',
      '[data-price]'
    ];
    
    for (const selector of priceSelectors) {
      const priceElement = productElement.querySelector(selector);
      if (priceElement) {
        const priceText = priceElement.textContent || priceElement.dataset.price;
        const price = this.parsePrice(priceText);
        if (price) return price;
      }
    }
    
    return null;
  }

  /**
   * Extract compare at price from product element
   */
  extractCompareAtPrice(productElement) {
    const compareAtPriceSelectors = [
      '.price--compare .price-item, .price-item--compare, [data-compare-price]',
      '.product-compare-price',
      '.compare-price .money',
      '[data-product-compare-price]',
      '.price-compare',
      '[data-compare-price]'
    ];
    
    for (const selector of compareAtPriceSelectors) {
      const compareElement = productElement.querySelector(selector);
      if (compareElement) {
        const compareText = compareElement.textContent || compareElement.dataset.comparePrice;
        const comparePrice = this.parsePrice(compareText);
        if (comparePrice) return comparePrice;
      }
    }
    
    return null;
  }

  /**
   * Parse price text to number
   */
  parsePrice(priceText) {
    if (!priceText) return null;
    
    const cleaned = priceText
      .replace(/[^\d,.]/g, '')
      .replace(/,/g, '.')
      .replace(/\.$/, '');
    
    const price = parseFloat(cleaned);
    return isNaN(price) ? null : price;
  }

  /**
   * Update badge display
   */
  updateBadge(discountData, productElement) {
    console.log('🔄 Updating badge with data:', discountData, productElement ? 'Scoped' : 'Global');
    
    let targetBadges = [];
    let targetContainers = [];
    
    if (productElement) {
      // Scoped update - find badges within the product element
      targetBadges = productElement.querySelectorAll(this.badgeSelector);
      targetContainers = productElement.querySelectorAll('.discount-badge-dynm');
    } else {
      // Fallback to global update (legacy behavior, but try to match by ID if possible)
      if (discountData.productId) {
         const specificContainers = document.querySelectorAll(`.discount-badge-dynm[data-product-id="${discountData.productId}"]`);
         if (specificContainers.length > 0) {
             targetContainers = Array.from(specificContainers);
             targetContainers.forEach(container => {
                 const badge = container.querySelector(this.badgeSelector);
                 if (badge) targetBadges.push(badge);
             });
         }
      }
      
      // If still no targets and no product element, fallback to global (dangerous but preserves old behavior if called without args)
      if (targetBadges.length === 0 && targetContainers.length === 0) {
        console.warn('⚠️ No target product element or ID match found, falling back to global update');
        targetBadges = document.querySelectorAll(this.badgeSelector);
        targetContainers = document.querySelectorAll('.discount-badge-dynm');
      }
    }
    
    targetBadges.forEach((badge, index) => {
      badge.textContent = discountData.discountPercentage;
      
      // Add animation
      badge.classList.add('discount-badge-updating');
      setTimeout(() => badge.classList.remove('discount-badge-updating'), 300);
    });
    
    // Show/hide badge containers
    targetContainers.forEach((container, index) => {
      if (discountData.hasDiscount) {
        container.style.display = 'flex';
      } else {
        container.style.display = 'none';
      }
    });
  }

  /**
   * Highlight the active product
   */
  highlightProduct(productElement) {
    // Remove highlighting from all products
    document.querySelectorAll(this.productItemSelector).forEach(el => {
      el.classList.remove('product-item--active');
    });
    
    // Add highlighting to current product
    productElement.classList.add('product-item--active');
  }
}

// Initialize for collection pages
if (!window.__collectionDynamicBadgeInit) {
  window.__collectionDynamicBadgeInit = true;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      console.log('DOM loaded, initializing collection dynamic badge');
      new CollectionDynamicBadge();
    });
  } else {
    console.log('DOM already loaded, initializing collection dynamic badge');
    new CollectionDynamicBadge();
  }
}
